import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

// The three launch locales. Keep `code` in sync with the `supportedLngs`
// entries in i18n/index.ts. `nativeName` is shown in the dropdown so each
// option reads natural to a speaker of that language.
const LANGUAGES = [
  { code: 'fr', nativeName: 'Français' },
  { code: 'en', nativeName: 'English' },
  { code: 'de', nativeName: 'Deutsch' },
] as const;

type LanguageCode = (typeof LANGUAGES)[number]['code'];

/**
 * Compact language picker for the public navbar. Shows the active language
 * code (uppercase) with a chevron; opens a dropdown with native names and
 * an "Active" badge on the current selection.
 */
export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  // i18next reports the full negotiated language (e.g. "en-US"); we always
  // surface the base two-letter code so it matches our supported list.
  const currentCode = (i18n.resolvedLanguage ?? i18n.language ?? 'fr')
    .slice(0, 2)
    .toLowerCase() as LanguageCode;
  const current =
    LANGUAGES.find((l) => l.code === currentCode) ?? LANGUAGES[0];

  const close = () => setAnchor(null);

  const handlePick = (code: LanguageCode) => {
    close();
    if (code !== currentCode) {
      void i18n.changeLanguage(code);
    }
  };

  return (
    <>
      <Button
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={
          <KeyboardArrowDownRoundedIcon
            sx={{
              transition: 'transform 200ms ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : undefined}
        aria-label={t('nav.language')}
        sx={{
          minWidth: 0,
          borderRadius: 999,
          px: 1.5,
          py: 0.4,
          fontWeight: 700,
          letterSpacing: '0.05em',
          fontSize: '0.8125rem',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        {current.code.toUpperCase()}
      </Button>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // MUI v5 Menu: `slotProps.list` isn't in the type yet — keep the
        // paper override here and configure the inner MenuList through
        // MenuListProps for the `role="listbox"` accessibility hint.
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 240,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 12px 32px rgba(0,0,0,0.55)'
                  : '0 12px 32px rgba(26,24,22,0.12)',
            },
          },
        }}
        MenuListProps={{
          sx: { py: 0.5 },
          role: 'listbox',
        }}
      >
        {LANGUAGES.map((lang) => {
          const isActive = lang.code === currentCode;
          return (
            <MenuItem
              key={lang.code}
              onClick={() => handlePick(lang.code)}
              role="option"
              aria-selected={isActive}
              sx={{ py: 1.25, px: 1.5 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{ width: '100%' }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    bgcolor: (theme) =>
                      isActive
                        ? theme.palette.primary.main
                        : theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(26,24,22,0.06)',
                    color: isActive ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  {lang.code.toUpperCase()}
                </Avatar>
                <Typography
                  sx={{ flex: 1, fontWeight: isActive ? 700 : 500 }}
                >
                  {lang.nativeName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', letterSpacing: '0.05em' }}
                >
                  {lang.code}
                </Typography>
                {isActive && (
                  <Chip
                    label={t('common.active', { defaultValue: 'Active' })}
                    size="small"
                    color="primary"
                    sx={{
                      height: 22,
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      px: 0.5,
                    }}
                  />
                )}
                {!isActive && <Box sx={{ width: 0 }} />}
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
